import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Entry, Player, Team, PoolTeam,
  WORLD_CUP_KNOCKOUT_ROUNDS, WorldCupKnockoutRoundKey,
} from '../types/pool';
import { CountryFlag } from './CountryFlag';
import { api } from '../services/api';

/**
 * Admin editor for knockout (DAILY_PICKS) entries.
 * Shows entry status, buy-back count, and picks grouped by date.
 * Allows editing status, removing/changing individual picks, and modifying results.
 */
function KnockoutPicksEditor({ picks, onChange }: { picks: any; onChange: (p: any) => void }) {
  const dailyPicks = picks.picks || {};
  const dates = Object.keys(dailyPicks).sort();
  const [newDate, setNewDate] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamSeed, setNewTeamSeed] = useState('');
  const [newTeamId, setNewTeamId] = useState('');

  const updateStatus = (status: string) => {
    onChange({ ...picks, status });
  };

  const updateBuyBackCount = (count: number) => {
    onChange({ ...picks, buyBackCount: Math.max(0, Math.min(3, count)) });
  };

  const updatePickResult = (date: string, pickIdx: number, result: string) => {
    const newDailyPicks = { ...dailyPicks };
    const dayPicks = [...(newDailyPicks[date] || [])];
    dayPicks[pickIdx] = { ...dayPicks[pickIdx], result };
    newDailyPicks[date] = dayPicks;
    onChange({ ...picks, picks: newDailyPicks });
  };

  const removePick = (date: string, pickIdx: number) => {
    const newDailyPicks = { ...dailyPicks };
    const dayPicks = [...(newDailyPicks[date] || [])];
    const removedTeamId = dayPicks[pickIdx]?.teamId;
    dayPicks.splice(pickIdx, 1);
    if (dayPicks.length === 0) {
      delete newDailyPicks[date];
    } else {
      newDailyPicks[date] = dayPicks;
    }
    // Also remove from usedTeams
    const usedTeams = (picks.usedTeams || []).filter((id: string) => id !== removedTeamId);
    onChange({ ...picks, picks: newDailyPicks, usedTeams });
  };

  const clearEliminatedOn = () => {
    onChange({ ...picks, eliminatedOn: null });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Knockout Entry</h3>

      {/* Status Controls */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={picks.status || 'ALIVE'}
            onChange={(e) => updateStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="ALIVE">ALIVE</option>
            <option value="BOUGHT_BACK">BOUGHT_BACK</option>
            <option value="ELIMINATED">ELIMINATED</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Buy-Back Count</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateBuyBackCount((picks.buyBackCount || 0) - 1)}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >−</button>
            <span className="text-lg font-bold w-8 text-center">{picks.buyBackCount || 0}</span>
            <button
              type="button"
              onClick={() => updateBuyBackCount((picks.buyBackCount || 0) + 1)}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >+</button>
            <span className="text-sm text-gray-500">/ 3</span>
          </div>
        </div>
      </div>

      {picks.eliminatedOn && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Eliminated on: <strong>{picks.eliminatedOn}</strong></span>
          <button
            type="button"
            onClick={clearEliminatedOn}
            className="text-red-500 hover:text-red-700 text-xs underline"
          >Clear</button>
        </div>
      )}

      {/* Daily Picks */}
      <div className="space-y-3">
        <h4 className="font-medium text-gray-800">Daily Picks</h4>
        {dates.length === 0 ? (
          <p className="text-sm text-gray-500">No picks yet</p>
        ) : (
          dates.map(date => {
            const dayPicks = dailyPicks[date] || [];
            return (
              <div key={date} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">
                    {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">({date})</span>
                </div>
                <div className="p-3 space-y-2">
                  {dayPicks.map((pick: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between bg-white border border-gray-100 rounded p-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-500">({pick.seed || '?'})</span>
                        <span className="text-sm font-medium text-gray-900">{pick.teamName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={pick.result || 'PENDING'}
                          onChange={(e) => updatePickResult(date, idx, e.target.value)}
                          className={`text-xs px-2 py-1 rounded border ${
                            pick.result === 'WIN' ? 'bg-green-50 border-green-300 text-green-800' :
                            pick.result === 'LOSS' ? 'bg-red-50 border-red-300 text-red-800' :
                            'bg-gray-50 border-gray-300 text-gray-700'
                          }`}
                        >
                          <option value="PENDING">Pending</option>
                          <option value="WIN">Win</option>
                          <option value="LOSS">Loss</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removePick(date, idx)}
                          className="text-red-400 hover:text-red-600 text-xs"
                          title="Remove pick"
                        >✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Pick to Date */}
      <div className="border border-dashed border-gray-300 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-2">Add Pick</h4>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Date (YYYY-MM-DD)</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Team Name</label>
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder="e.g. Duke"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Seed</label>
            <input
              type="number"
              value={newTeamSeed}
              onChange={(e) => setNewTeamSeed(e.target.value)}
              placeholder="1-16"
              min="1"
              max="16"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Team ID (from DB)</label>
            <input
              type="text"
              value={newTeamId}
              onChange={(e) => setNewTeamId(e.target.value)}
              placeholder="UUID"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            if (!newDate || !newTeamName) return;
            const newDailyPicks = { ...dailyPicks };
            if (!newDailyPicks[newDate]) newDailyPicks[newDate] = [];
            newDailyPicks[newDate] = [
              ...newDailyPicks[newDate],
              {
                teamId: newTeamId || `manual-${Date.now()}`,
                teamName: newTeamName,
                seed: parseInt(newTeamSeed) || 16,
                result: 'PENDING',
              },
            ];
            const usedTeams = [...(picks.usedTeams || [])];
            if (newTeamId && !usedTeams.includes(newTeamId)) usedTeams.push(newTeamId);
            onChange({ ...picks, picks: newDailyPicks, usedTeams });
            setNewTeamName('');
            setNewTeamSeed('');
            setNewTeamId('');
          }}
          disabled={!newDate || !newTeamName}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Add Pick
        </button>
      </div>

      {/* Used Teams */}
      <div className="text-xs text-gray-500">
        Used teams: {(picks.usedTeams || []).length}
      </div>
    </div>
  );
}

/**
 * Admin editor for WORLD_CUP_SURVIVOR entries.
 *
 * Lets admin: (1) edit the entry status + eliminatedRound, (2) swap a group
 * stage pick (e.g. user picked the wrong country), and (3) set/change a
 * knockout-round pick (the common "user forgot to submit R32" case). Each
 * knockout pick has a per-round team dropdown sourced from the pool's teams.
 *
 * Backend's WORLD_CUP_SURVIVOR validator runs in admin-override mode (via
 * the existing /admin/pools/:id/entries/:entryId endpoint) so eliminated-team
 * and one-and-done constraints are relaxed — admin can fix data the user
 * couldn't.
 */
function WorldCupSurvivorEditor({
  picks,
  onChange,
  poolTeams,
}: {
  picks: any;
  onChange: (p: any) => void;
  poolTeams: PoolTeam[];
}) {
  const groupStageTeams: any[] = Array.isArray(picks?.groupStageTeams) ? picks.groupStageTeams : [];
  const knockoutPicks: Record<string, any> = picks?.knockoutPicks || {};

  // Quick lookup: teamId → PoolTeam (for converting a dropdown selection into
  // the pick shape we store).
  const teamById = new Map(poolTeams.map((pt) => [pt.team.id, pt]));

  // Sort once for the dropdown — by group letter then seed, for findability.
  const sortedTeams = [...poolTeams].sort((a, b) => {
    const ga = (a.metadata?.group as string) || 'Z';
    const gb = (b.metadata?.group as string) || 'Z';
    if (ga !== gb) return ga.localeCompare(gb);
    return (a.seed ?? 99) - (b.seed ?? 99);
  });

  function updateStatus(status: string) {
    onChange({ ...picks, status });
  }
  function updateEliminatedRound(round: string) {
    onChange({ ...picks, eliminatedRound: round || null });
  }

  function updateGroupPick(idx: number, teamId: string) {
    const pt = teamById.get(teamId);
    if (!pt) return;
    const next = [...groupStageTeams];
    next[idx] = {
      ...(next[idx] || {}),
      teamId: pt.team.id,
      teamName: pt.team.name,
      shortName: pt.team.shortName,
      seed: pt.seed ?? 12,
      isoCode: (pt.metadata?.isoCode as string) ?? '',
      advanced: next[idx]?.advanced ?? 'PENDING',
    };
    onChange({ ...picks, groupStageTeams: next });
  }

  function updateGroupPickAdvanced(idx: number, advanced: string) {
    const next = [...groupStageTeams];
    next[idx] = { ...(next[idx] || {}), advanced };
    onChange({ ...picks, groupStageTeams: next });
  }

  function setKnockoutPick(round: WorldCupKnockoutRoundKey, teamId: string) {
    if (!teamId) {
      // Clear the round
      const next = { ...knockoutPicks };
      delete next[round];
      onChange({ ...picks, knockoutPicks: next });
      return;
    }
    const pt = teamById.get(teamId);
    if (!pt) return;
    onChange({
      ...picks,
      knockoutPicks: {
        ...knockoutPicks,
        [round]: {
          teamId: pt.team.id,
          teamName: pt.team.name,
          shortName: pt.team.shortName,
          seed: pt.seed ?? 12,
          isoCode: (pt.metadata?.isoCode as string) ?? '',
          result: knockoutPicks[round]?.result ?? 'PENDING',
        },
      },
    });
  }

  function setKnockoutResult(round: WorldCupKnockoutRoundKey, result: string) {
    if (!knockoutPicks[round]) return;
    onChange({
      ...picks,
      knockoutPicks: {
        ...knockoutPicks,
        [round]: { ...knockoutPicks[round], result },
      },
    });
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">World Cup Survivor Entry</h3>

      {/* Status + eliminatedRound */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={picks.status || 'ALIVE'}
            onChange={(e) => updateStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="ALIVE">ALIVE</option>
            <option value="ELIMINATED">ELIMINATED</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Eliminated Round</label>
          <select
            value={picks.eliminatedRound || ''}
            onChange={(e) => updateEliminatedRound(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">— None (still alive)</option>
            <option value="GROUP">GROUP</option>
            {WORLD_CUP_KNOCKOUT_ROUNDS.map((r) => (
              <option key={r.key} value={r.key}>{r.key}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Group Stage Picks */}
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Phase 1 — Group Stage (6 picks)</h4>
        {groupStageTeams.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No group-stage picks recorded.</p>
        ) : (
          <div className="space-y-2">
            {groupStageTeams.map((gp, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded p-2">
                <span className="text-xs text-gray-500 font-semibold w-6">#{idx + 1}</span>
                {gp.isoCode && <CountryFlag iso={gp.isoCode} alt={gp.teamName} size="sm" rounded />}
                <select
                  value={gp.teamId || ''}
                  onChange={(e) => updateGroupPick(idx, e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="">— Select team —</option>
                  {sortedTeams.map((pt) => (
                    <option key={pt.team.id} value={pt.team.id}>
                      Group {pt.metadata?.group ?? '?'} — {pt.team.name} (Seed {pt.seed ?? '?'})
                    </option>
                  ))}
                </select>
                <select
                  value={gp.advanced || 'PENDING'}
                  onChange={(e) => updateGroupPickAdvanced(idx, e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                  title="Advanced state"
                >
                  <option value="PENDING">PENDING</option>
                  <option value="YES">YES (advanced)</option>
                  <option value="NO">NO (eliminated)</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Knockout Picks */}
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Phase 2 — Knockout (one per round)</h4>
        <p className="text-xs text-gray-500 mb-3">
          Use "— None —" to clear a round. The most common use is setting an R32 pick for a
          user who forgot to submit before kickoff.
        </p>
        <div className="space-y-2">
          {WORLD_CUP_KNOCKOUT_ROUNDS.map((r) => {
            const pick = knockoutPicks[r.key];
            return (
              <div key={r.key} className="flex items-center gap-2 bg-gray-50 rounded p-2">
                <span className="text-xs text-gray-600 font-semibold w-32">{r.label}</span>
                {pick?.isoCode && <CountryFlag iso={pick.isoCode} alt={pick.teamName} size="sm" rounded />}
                <select
                  value={pick?.teamId || ''}
                  onChange={(e) => setKnockoutPick(r.key, e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="">— None —</option>
                  {sortedTeams.map((pt) => (
                    <option key={pt.team.id} value={pt.team.id}>
                      Group {pt.metadata?.group ?? '?'} — {pt.team.name} (Seed {pt.seed ?? '?'})
                    </option>
                  ))}
                </select>
                <select
                  value={pick?.result || 'PENDING'}
                  onChange={(e) => setKnockoutResult(r.key, e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                  disabled={!pick}
                  title="Result of the match"
                >
                  <option value="PENDING">PENDING</option>
                  <option value="WIN">WIN</option>
                  <option value="LOSS">LOSS</option>
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface AdminEditPicksModalProps {
  entry: Entry;
  poolId: string;
  poolType: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminEditPicksModal({
  entry,
  poolId,
  poolType,
  onClose,
  onSuccess,
}: AdminEditPicksModalProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  // Rich PoolTeam[] (with metadata.isoCode/group + seed) — needed for the WC
  // editors. getPoolTeams returns a flat shape without metadata, so we fall
  // back to fetching the whole pool which DOES include rich teams.
  const [poolTeams, setPoolTeams] = useState<PoolTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [entryName, setEntryName] = useState(entry.entryName || '');
  const [picks, setPicks] = useState<any>(entry.picks);

  useEffect(() => {
    loadData();
  }, [poolId]);

  async function loadData() {
    try {
      setLoading(true);
      const [playersData, teamsData, poolData] = await Promise.all([
        api.getPoolPlayers(poolId),
        api.getPoolTeams(poolId),
        // getPool includes the rich teams array (PoolTeam[] w/ metadata) which
        // the WC editor needs for flags + group letters. Roster editors keep
        // using the flat `teams` array so nothing else regresses.
        api.getPool(poolId).catch(() => null),
      ]);
      setPlayers(playersData.players || []);
      setTeams(teamsData);
      setPoolTeams((poolData?.teams as PoolTeam[]) || []);
    } catch (err) {
      console.error('Failed to load pool data:', err);
      setError('Failed to load pool data');
    } finally {
      setLoading(false);
    }
  }

  function handlePlayerChange(position: string, playerId: string) {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    setPicks({
      ...picks,
      positions: {
        ...picks.positions,
        [position]: {
          playerId: player.id,
          playerName: player.name,
          teamId: player.teamId,
          teamName: player.team.name,
          position: player.position,
        },
      },
    });
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      
      const token = await getAccessTokenSilently();
      
      await api.adminUpdateEntry(poolId, entry.id, {
        picks,
        entryName,
      }, token);
      
      alert('Picks updated successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update picks:', err);
      setError(err instanceof Error ? err.message : 'Failed to update picks');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Get position requirements based on pool type
  const getPositionRequirements = () => {
    if (poolType === 'CFP') {
      return ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE', 'FLEX1', 'FLEX2', 'K', 'DEF'];
    } else if (poolType === 'NFL_PLAYOFF') {
      return ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE', 'FLEX1', 'FLEX2', 'FLEX3', 'FLEX4', 'K', 'DEF'];
    }
    return Object.keys(picks?.positions || {});
  };

  const positions = getPositionRequirements();

  // Get available players for a position
  const getAvailablePlayers = (position: string) => {
    if (position === 'DEF') {
      return teams.map(team => ({
        id: `DEF-${team.id}`,
        name: `${team.name} Defense`,
        teamId: team.id,
        team: team,
        position: 'DEF',
      }));
    }

    // For FLEX positions, allow RB/WR/TE
    if (position.startsWith('FLEX')) {
      return players.filter(p => ['RB', 'WR', 'TE'].includes(p.position));
    }

    // For specific positions
    if (position.startsWith('QB')) return players.filter(p => p.position === 'QB');
    if (position.startsWith('RB')) return players.filter(p => p.position === 'RB');
    if (position.startsWith('WR')) return players.filter(p => p.position === 'WR');
    if (position.startsWith('TE')) return players.filter(p => p.position === 'TE');
    if (position.startsWith('K')) return players.filter(p => p.position === 'K');

    return players;
  };

  // Get teams already used
  const usedTeams = new Set(
    Object.values(picks?.positions || {}).map((pick: any) => pick.teamId)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div>
            <h2 className="text-2xl font-bold">Admin: Edit Picks</h2>
            <p className="text-red-100 text-sm mt-1">
              Editing entry for {(entry as any).displayName || (entry as any).username || 'User'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-6 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Admin Override:</strong> You can edit picks even after the deadline. 
                This bypasses normal validation. Scores will be recalculated automatically.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Entry Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entry Name
            </label>
            <input
              type="text"
              value={entryName}
              onChange={(e) => setEntryName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="My Entry"
            />
          </div>

          {/* Knockout Picks Editor */}
          {picks?.type === 'DAILY_PICKS' ? (
            <KnockoutPicksEditor picks={picks} onChange={setPicks} />
          ) : picks?.type === 'WORLD_CUP_SURVIVOR' ? (
            <WorldCupSurvivorEditor picks={picks} onChange={setPicks} poolTeams={poolTeams} />
          ) : (
          /* Roster Picks Editor */
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Roster Picks</h3>

            {positions.map((position) => {
              const currentPick = picks?.positions?.[position];
              const availablePlayers = getAvailablePlayers(position);

              return (
                <div key={position} className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {position}
                  </label>

                  <select
                    value={currentPick?.playerId || ''}
                    onChange={(e) => handlePlayerChange(position, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a player...</option>
                    {availablePlayers.map((player) => {
                      const isTeamUsed = usedTeams.has(player.teamId) && player.teamId !== currentPick?.teamId;
                      return (
                        <option
                          key={player.id}
                          value={player.id}
                          disabled={isTeamUsed}
                          style={{ color: isTeamUsed ? '#ccc' : 'inherit' }}
                        >
                          {player.name} - {player.team.name} ({player.position})
                          {isTeamUsed ? ' [Team already used]' : ''}
                        </option>
                      );
                    })}
                  </select>

                  {currentPick && (
                    <p className="mt-1 text-xs text-gray-500">
                      Current: {currentPick.playerName} ({currentPick.teamName})
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
